import { cn } from "components/lib/utils";
import { Button } from "components/shadcn/ui/button";
import { ScrollArea } from "components/shadcn/ui/scrollArea";
import { generateTimeAgo } from "helpers";
import { BookmarkCheck, Loader2 } from "lucide-react";
import React, { useEffect } from "react";
import { useNotifications } from "./useNotifications";
import { Badge } from "components/shadcn/ui/badge";
import ToolTipWrapper from "components/common/helper/tooltipWrapper";
import { NotificationListItem } from "./types";

interface NotificationPopoverContentProps {
  onOpen?: boolean;
  onClose?: () => void;
}

const NotificationPopoverContent = ({
  onOpen = false,
  onClose,
}: NotificationPopoverContentProps) => {
  const {
    loading,
    notifications,

    handleCtaClick,
    getNotificationsList,
    markAllAsRead,
    markAsRead,
  } = useNotifications();

  useEffect(() => {
    if (onOpen) {
      getNotificationsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onOpen]);

  const sortedNotifications = [...notifications].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const hasUnread = sortedNotifications.some((n) => !n.isRead);

  const handleViewDetails = (notification: NotificationListItem) => {
    handleCtaClick(notification);
    if (onClose) onClose();
  };

  return (
    <div className="w-80 p-0">
      <div className="p-1 flex justify-between items-center border-b mb-2">
        <h4 className="font-semibold">Notifications</h4>

        {hasUnread && (
          <ToolTipWrapper content="Mark all as read">
            <Button
              variant="ghost"
              onClick={markAllAsRead}
              className="text-sm text-gray-800 cursor-pointer"
            >
              <BookmarkCheck className="text-primary-700" />
              Mark All As Read
            </Button>
          </ToolTipWrapper>
        )}
      </div>

      <ScrollArea className="h-80 transition-all">
        {loading ? (
          <div className="flex items-center justify-center h-80">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : sortedNotifications.length === 0 ? (
          <div className="flex items-center justify-center h-80">
            <p className="text-sm text-muted-foreground">
              No new notifications
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 px-2 mt-2">
            {sortedNotifications.map((notification) => {
              const notifCard = (
                <div
                  key={notification.id}
                  className={cn(
                    "p-3 border rounded-md flex justify-between items-start",
                    !notification.isRead
                      ? "bg-blue-50 cursor-pointer"
                      : "bg-white"
                  )}
                  {...(!notification.isRead && {
                    onClick: () => markAsRead(notification.id),
                  })}
                >
                  <div className="flex flex-col flex-1 gap-1">
                    <p className="text-sm font-medium">{notification.title}</p>

                    <div className="w-full flex flex-row justify-between">
                      <p className="text-xs text-muted-foreground">
                        {generateTimeAgo(new Date(notification.createdAt))}
                      </p>

                      {notification.hasCta && notification.ctaLabel && (
                        <ToolTipWrapper content="View Details">
                          <Badge
                            variant="primary"
                            onClick={() => handleViewDetails(notification)}
                            className="text-xs font-semibold hover:underline bg-[--primary-50] text-gray-800 cursor-pointer"
                          >
                            {notification.ctaLabel}
                          </Badge>
                        </ToolTipWrapper>
                      )}
                    </div>
                  </div>
                </div>
              );

              return !notification.isRead ? (
                <ToolTipWrapper
                  key={notification.id}
                  content="Click to Mark as Read"
                >
                  {notifCard}
                </ToolTipWrapper>
              ) : (
                notifCard
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default NotificationPopoverContent;
